import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import {
  Route,
  RouterProvider,
  Routes,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import { JunoAnalytics } from '@juno/analytics';
import { AutomationTool, EmailCampaignBuilder } from '@juno/automation';
import { getGetMeQueryKey, useGetLatestTermsOfUseLog, useUpdateUser } from '@juno/client-api';
import { FeatureConfigTypeEnum, JunoUser } from '@juno/client-api/model';
import { CommunityGroupPanel, GroupPage } from '@juno/community';
import { ImportExport } from '@juno/import-export';
import { AdminCoursePanel, CoursePage, LessonPage } from '@juno/learning';
import { PageBuilder, ReportPreview, TagsPanel } from '@juno/modules';
import {
  CallDeclinedDialog,
  IncomingCall,
  OutgoingCall,
  SessionPage,
  SessionPanel,
} from '@juno/sessions';
import { DeactivatedAccountDialog, FeatureNotAvailablePage, JunoSpin } from '@juno/ui';
import {
  MutationAction,
  logoutUser,
  onMutation,
  useSessionContext,
  useSettings,
} from '@juno/utils';
import AccessAdmin from './components/Admin/Access';
import CoursesAdmin from './components/Admin/Courses';
import DeveloperAdmin from './components/Admin/Dev';
import General from './components/Admin/General';
import GroupsAdmin from './components/Admin/Groups';
import LibraryAdmin from './components/Admin/Library';
import ModerationAdmin from './components/Admin/Moderation';
import NavigationAdmin from './components/Admin/Navigation';
import PagesAdmin from './components/Admin/Pages';
import PrivacyAdmin from './components/Admin/Privacy';
import ProfileAdmin from './components/Admin/Profile';
import SessionsAdmin from './components/Admin/Sessions';
import SpeakersAdmin from './components/Admin/Speakers';
import SpeakerAdmin from './components/Admin/Speakers/Speaker';
import UsersAdmin from './components/Admin/Users';
import Callback from './components/Callback';
import DefaultRedirect from './components/DefaultRedirect';
import DynamicPage from './components/DynamicPage';
import Layout from './components/Layout';
import SpeakerPage from './components/SpeakerPage';
import TermsOfUseDialog from './components/TermsOfUseDialog';
import UserProfile from './components/UserProfile';
import { UserProfileProvider } from './components/UserProfile/UserProfileContext';
import UserSettings from './components/UserProfile/UserSettings';
import YourCourses from './components/YourCourses';
import YourSchedule from './components/YourSchedule';

const PrivateRoutes: React.FC = () => {
  const { configs, isAdmin, site, platform, user, enableLearning, enableLibrary } = useSettings();

  const { callState } = useSessionContext();

  const [showTermsOfUse, setShowTermsOfUse] = useState(false);
  const termsOfUseConfig = configs?.find((c) => c.type === FeatureConfigTypeEnum.terms_of_use);
  const queryClient = useQueryClient();
  const refetchUser = () => {
    queryClient.invalidateQueries({
      queryKey: getGetMeQueryKey(platform?.id || ''),
    });
  };

  const updateUser = useUpdateUser(onMutation(MutationAction.UPDATE, '', refetchUser));

  const onAccept = async () => {
    try {
      // force the user to accept the terms of use even if we error
      await updateUser.mutateAsync({
        platformId: platform?.id || '',
        userId: user?.id || '',
        data: {
          ...user,
          terms_accepted_timestamp: new Date().toISOString(),
        } as JunoUser,
      });
      setShowTermsOfUse(false);
    } catch (e) {
      console.error(e);
    }
  };

  const { data: latestToULog, isLoading: latestToULoading } = useGetLatestTermsOfUseLog(
    site?.id || '',
    {
      query: {
        enabled: !!(
          termsOfUseConfig &&
          termsOfUseConfig.config?.enable_tou &&
          termsOfUseConfig.config?.body !== ''
        ),
      },
    },
  );

  useEffect(() => {
    if (
      !user?.terms_accepted_timestamp &&
      termsOfUseConfig &&
      termsOfUseConfig.config?.enable_tou &&
      termsOfUseConfig.config?.body !== ''
    ) {
      setShowTermsOfUse(true);
    }

    if (
      termsOfUseConfig &&
      user?.terms_accepted_timestamp &&
      latestToULog?.timestamp &&
      termsOfUseConfig.config?.enable_tou &&
      new Date(user.terms_accepted_timestamp) < new Date(latestToULog.timestamp)
    ) {
      setShowTermsOfUse(true);
    }
  }, [user, termsOfUseConfig, latestToULoading]);

  if (!configs || !site || !user) return <JunoSpin />;

  // only show if configcat has it on
  if (user && showTermsOfUse) return <TermsOfUseDialog onAccept={onAccept} />;

  return (
    <Layout siteId={site.id}>
      {/* If the user has been deactivated we lock on the deactivation dialog */}
      {!user?.active ? (
        <Routes>
          <Route
            path='*'
            element={
              <DeactivatedAccountDialog
                open={true}
                onClose={() => {
                  logoutUser(platform?.id);
                }}
              />
            }
          />
        </Routes>
      ) : (
        <Routes>
          <Route path='/' element={<DefaultRedirect />} />
          <Route
            path={'/:siteSlug/:pageSlug'}
            element={<DynamicPage site={site} configs={configs} />}
          />
          <Route
            path={'/:siteSlug/:pageSlug/:discussionSlug'}
            element={<DynamicPage site={site} configs={configs} />}
          />
          <Route
            path={'/:siteSlug/library/:pageSlug'}
            element={
              enableLibrary ? (
                <DynamicPage site={site} configs={configs} />
              ) : (
                <FeatureNotAvailablePage />
              )
            }
          />
          <Route path={'/:siteSlug/sessions/:sessionSlug'} element={<SessionPage />} />
          <Route path='/:siteSlug/sessions/:sessionSlug/edit' element={<SessionPanel />} />
          <Route
            path={'/:siteSlug/speakers/:slug'}
            element={<SpeakerPage site={site} configs={configs} />}
          />

          <Route
            path={'/:siteSlug/learning/courses/:courseSlug'}
            element={
              enableLearning ? (
                <CoursePage siteId={site.id} settings={{ user: user }} />
              ) : (
                <FeatureNotAvailablePage />
              )
            }
          />
          <Route
            path={'/:siteSlug/learning/courses/:courseSlug/:lessonSlug'}
            element={enableLearning ? <LessonPage siteId={site.id} /> : <FeatureNotAvailablePage />}
          />
          <Route
            path={'/:siteSlug/you/courses'}
            element={enableLearning ? <YourCourses /> : <FeatureNotAvailablePage />}
          />
          <Route
            path='/:siteSlug/community/edit_group/:groupSlug'
            element={<CommunityGroupPanel site={site} />}
          >
            <Route
              path='/:siteSlug/community/edit_group/:groupSlug/:tabSlug'
              element={<CommunityGroupPanel site={site} />}
            />
          </Route>
          {/* Routes for single group and tabs on group page */}
          <Route
            path='/:siteSlug/community/groups/:groupSlug'
            element={<GroupPage siteId={site.id} platformId={site.platform_id} />}
          >
            <Route
              path=':groupTabSlug'
              element={<GroupPage siteId={site.id} platformId={site.platform_id} />}
            >
              <Route
                path={`:discussionSlug`}
                element={<GroupPage siteId={site.id} platformId={site.platform_id} />}
              />
            </Route>
          </Route>

          {/* --- */}
          <Route path={'/:siteSlug/user/:userId/settings'} element={<UserSettings />} />
          <Route
            path={'/:siteSlug/user/:userId'}
            element={
              <UserProfileProvider>
                <UserProfile />
              </UserProfileProvider>
            }
          />

          <Route path={'/:siteSlug/you/groups'} element={'your groups'} />
          <Route path={'/:siteSlug/you/schedule'} element={<YourSchedule />} />
          <Route path={'/:siteSlug/you/saved_content'} element={'your saved content'} />
          <Route path={'/:siteSlug/admin/sessions/:sessionSlug'} element={<SessionPanel />} />
          <Route path={'/:siteSlug/admin/sessions'} element={<SessionsAdmin />} />
          {isAdmin && (
            <>
              <Route
                path={'/:siteSlug/admin/learning/:courseSlug/:tabSlug'}
                element={
                  enableLearning ? <AdminCoursePanel site={site} /> : <FeatureNotAvailablePage />
                }
              />
              <Route
                path={'/:siteSlug/admin/learning/:courseSlug/lessons/:lessonSlug'}
                element={
                  enableLearning ? <AdminCoursePanel site={site} /> : <FeatureNotAvailablePage />
                }
              />
              <Route
                path={'/:siteSlug/admin/learning/:courseSlug'}
                element={
                  enableLearning ? <AdminCoursePanel site={site} /> : <FeatureNotAvailablePage />
                }
              />
              <Route
                path={'/:siteSlug/admin/learning'}
                element={
                  enableLearning ? <CoursesAdmin site={site} /> : <FeatureNotAvailablePage />
                }
              />

              <Route
                path={'/:siteSlug/admin/community/groups'}
                element={<GroupsAdmin site={site} />}
              />

              <Route
                path='/:siteSlug/admin/community/groups/:groupSlug'
                element={<CommunityGroupPanel site={site} />}
              >
                <Route
                  path='/:siteSlug/admin/community/groups/:groupSlug/:tabSlug'
                  element={<CommunityGroupPanel site={site} />}
                />
              </Route>
              <Route path={'/:siteSlug/admin/speakers'} element={<SpeakersAdmin />} />
              <Route path={'/:siteSlug/admin/speakers/:slug'} element={<SpeakerAdmin />} />

              <Route path={'/:siteSlug/admin'} element={<General site={site} />} />
              {/* // TODO this is a duplicate but its not styled the same? However this one loads prerequisites... */}
              {/* <Route path={'/:siteSlug/admin/learning'} element={<AdminCoursesPanel site={site} />} /> */}
              <Route path={'/:siteSlug/admin/community'} element={<GroupsAdmin site={site} />} />
              <Route
                path={'/:siteSlug/admin/pages/:pageSlug'}
                element={<PageBuilder siteId={site.id} />}
              />
              <Route path={'/:siteSlug/admin/pages'} element={<PagesAdmin site={site} />} />
              <Route path={'/:siteSlug/admin/navigation'} element={<NavigationAdmin />} />
              <Route path={'/:siteSlug/admin/profile'} element={<ProfileAdmin />} />
              <Route
                path={'/:siteSlug/admin/library'}
                element={enableLibrary ? <LibraryAdmin site={site} /> : <FeatureNotAvailablePage />}
              />
              <Route path={'/:siteSlug/admin/moderation'} element={<ModerationAdmin />} />
              <Route path={'/:siteSlug/admin/users'} element={<UsersAdmin />} />
              <Route path={'/:siteSlug/admin/tags'} element={<TagsPanel site={site} />} />
              <Route path={'/:siteSlug/admin/access/:id'} element={<AccessAdmin />} />
              <Route path={'/:siteSlug/admin/access'} element={<AccessAdmin />} />
              <Route
                path={'/:siteSlug/admin/analytics'}
                element={<JunoAnalytics isSuperAdmin={false} />}
              />
              <Route path={'/:siteSlug/admin/super_admin'} element={<DeveloperAdmin />} />
              <Route path={'/:siteSlug/report_preview'} element={<ReportPreview />} />
              <Route path={'/:siteSlug/admin/automation_tool'} element={<AutomationTool />} />
              <Route path={'/:siteSlug/admin/privacy'} element={<PrivacyAdmin />} />
              <Route
                path={'/:siteSlug/admin/automation_tool/campaigns/:id'}
                element={<EmailCampaignBuilder />}
              />
              <Route
                path={'/:siteSlug/admin/import-export/*'}
                element={<ImportExport site={site} />}
              />
            </>
          )}
        </Routes>
      )}
      <IncomingCall />
      <OutgoingCall />
      <CallDeclinedDialog />
    </Layout>
  );
};

const JunoRoutesClient: React.FC = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path={'/:siteSlug/callback'} element={<Callback />} />
        <Route path={'/*'} element={<PrivateRoutes />} />
      </>,
    ),
  );

  return (
    <NuqsAdapter>
      <RouterProvider router={router} />
    </NuqsAdapter>
  );
};

export default JunoRoutesClient;
